import React from 'react';
import { Alert, Button, Card, Col, Row, Space, Tag, Tooltip, Divider, Typography } from 'antd';
import { CheckCircleOutlined } from '@ant-design/icons';
import { JiraStory } from '../types';

const { Title, Text } = Typography;

interface JiraStoriesViewerProps {
  project: any;
  errorMessage?: string | null;
  onGenerateJiraStories: () => void;
  generateJiraStoriesLoading: boolean;
  canEdit?: boolean;
  canCreate?: boolean;
  jiraStoriesApproved?: boolean;
  onApprovalChange?: () => void;
  onApproveJiraStories?: () => void;
  approveLoading?: boolean;
}

const JiraStoriesViewer: React.FC<JiraStoriesViewerProps> = ({
  project,
  errorMessage,
  onGenerateJiraStories,
  generateJiraStoriesLoading,
  canEdit = false,
  canCreate = false,
  jiraStoriesApproved = false,
  onApprovalChange,
  onApproveJiraStories,
  approveLoading = false
}) => {
  return (
    <div>
      {/* Jira Stories Tab Content */}
      {errorMessage && (
        <Alert message={errorMessage} type="error" showIcon style={{ marginBottom: 16 }} />
      )}
      {project.artifacts.jiraStories ? (
        <div>
          {!Array.isArray(project.artifacts.jiraStories) && project.artifacts.jiraStories.content ? (
            <div>
              {/* Header Information */}
              <Card 
                style={{ marginBottom: 24 }} 
                title="Project Overview"
              >
                <Row gutter={[16, 16]}>
                  <Col xs={24}>
                    <Space direction="vertical" size={8} style={{ width: '100%' }}>
                      <div>
                        <Text strong style={{ color: '#1890ff' }}>Artifact ID:</Text>
                        <br />
                        <Text>{project.artifacts.jiraStories.artifact_id}</Text>
                      </div>
                      <div>
                        <Text strong style={{ color: '#1890ff' }}>Project Title:</Text>
                        <br />
                        <Text>{project.artifacts.jiraStories.title}</Text>
                      </div>
                      {project.artifacts.jiraStoriesEditedTimestamp && (
                        <div>
                          <Text strong style={{ color: '#52c41a' }}>Last Updated:</Text>
                          <br />
                          <Text>{new Date(project.artifacts.jiraStoriesEditedTimestamp).toLocaleString()}</Text>
                        </div>
                      )}
                    </Space>
                  </Col>
                </Row>
              </Card>

              {/* Epics Section */}
              {project.artifacts.jiraStories.content.epics && project.artifacts.jiraStories.content.epics.length > 0 && (
                <Card title="Epics" style={{ marginBottom: 24 }}>
                  <Row gutter={[16, 24]}>
                    {project.artifacts.jiraStories.content.epics.map((epic: any, index: number) => (
                      <Col xs={24} sm={12} lg={8} xl={6} key={index}>
                        <Card
                          size="small"
                          hoverable
                          style={{ 
                            height: '100%', 
                            border: '1px solid #d9d9d9',
                            minHeight: '200px'
                          }}
                          headStyle={{
                            padding: '12px',
                            borderBottom: '1px solid #f0f0f0'
                          }}
                          bodyStyle={{
                            padding: '16px'
                          }}
                          title={
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                              <div>
                                <Tooltip title={epic.name} placement="top">
                                  <Text strong style={{ color: '#722ed1', fontSize: '14px', lineHeight: '1.3', display: 'block' }}>
                                    {epic.name.length > 60 ? `${epic.name.substring(0, 60)}...` : epic.name}
                                  </Text>
                                </Tooltip>
                              </div>
                              <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', alignItems: 'center' }}>
                                <Tag color="purple" style={{ fontSize: '11px', margin: 0 }}>{epic.epic_id}</Tag>
                                <Tag color="geekblue" style={{ fontSize: '11px', margin: 0, whiteSpace: 'nowrap' }}>
                                  Priority: {epic.priority}
                                </Tag>
                              </div>
                            </div>
                          }
                          extra={null}
                        >
                          <Space direction="vertical" size={8} style={{ width: '100%' }}>
                            <div>
                              <Text type="secondary">{epic.description}</Text>
                            </div>
                            <div>
                              <Text strong>Duration:</Text> <Text>{epic.estimated_duration}</Text>
                            </div>
                            {epic.linked_requirements && (
                              <div>
                                <Text strong>Linked Requirements:</Text>
                                <div style={{ marginTop: 4 }}>
                                  {epic.linked_requirements.map((req: string, i: number) => (
                                    <Tag key={i} color="blue" style={{ marginBottom: 4 }}>{req}</Tag>
                                  ))}
                                </div>
                              </div>
                            )}
                          </Space>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                </Card>
              )}

              {/* Stories Section */}
              {project.artifacts.jiraStories.content.stories && project.artifacts.jiraStories.content.stories.length > 0 && (
                <Card title="User Stories" style={{ marginBottom: 24 }}>
                  <Row gutter={[16, 16]}>
                    {project.artifacts.jiraStories.content.stories.map((story: any, index: number) => (
                      <Col xs={24} lg={12} xl={8} key={index}>
                        <Card
                          size="small"
                          hoverable
                          style={{ height: '100%', border: '1px solid #d9d9d9' }}
                          title={
                            <div>
                              <Tooltip title={story.title} placement="top" mouseEnterDelay={0.3}>
                                <Text strong style={{ color: '#fa541c', fontSize: '14px' }}>
                                  {story.title.length > 50 ? `${story.title.substring(0, 50)}...` : story.title}
                                </Text>
                              </Tooltip>
                              <br />
                              <div style={{ marginTop: 4, marginBottom: 5 }}>
                                <Tag color="volcano">{story.story_id}</Tag>
                                <Tag color="red">Priority: {story.priority}</Tag>
                              </div>
                            </div>
                          }
                          extra={null}
                        >
                          <Space direction="vertical" size={12} style={{ width: '100%' }}>
                            <div>
                              <Text strong style={{ color: '#52c41a' }}>User Story:</Text>
                              <div style={{ marginTop: 4, padding: '8px 12px', backgroundColor: '#f6ffed', borderRadius: '6px', border: '1px solid #b7eb8f' }}>
                                <Text>{story.description}</Text>
                              </div>
                            </div>
                            
                            <div>
                              <Text strong style={{ color: '#1890ff' }}>Acceptance Criteria:</Text>
                              <ul style={{ margin: '8px 0 0 0', paddingLeft: 24, backgroundColor: '#f0f9ff', padding: '12px 24px', borderRadius: '6px', border: '1px solid #91d5ff' }}>
                                {story.acceptance_criteria?.map((criteria: string, i: number) => (
                                  <li key={i} style={{ marginBottom: 8, lineHeight: '1.4' }}>
                                    <Text style={{ fontSize: '13px' }}>{criteria}</Text>
                                  </li>
                                ))}
                              </ul>
                            </div>

                            {story.definition_of_done && story.definition_of_done.length > 0 && (
                              <div>
                                <Text strong style={{ color: '#52c41a' }}>Definition of Done:</Text>
                                <ul style={{ margin: '8px 0 0 0', paddingLeft: 24, backgroundColor: '#f6ffed', padding: '12px 24px', borderRadius: '6px', border: '1px solid #b7eb8f' }}>
                                  {story.definition_of_done.map((dod: string, i: number) => (
                                    <li key={i} style={{ marginBottom: 8, lineHeight: '1.4' }}>
                                      <Text style={{ fontSize: '13px' }}>{dod}</Text>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}

                            {story.linked_requirement_id && story.linked_requirement_id.length > 0 && (
                              <div>
                                <Text strong style={{ color: '#722ed1' }}>Linked Requirements:</Text>
                                <div style={{ marginTop: 4 }}>
                                  {story.linked_requirement_id.map((reqId: string, i: number) => (
                                    <Tag key={i} color="purple">{reqId}</Tag>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Story Points after Linked Requirements */}
                            <div>
                              <Text strong style={{ color: '#eb2f96' }}>Story Points:</Text>
                              <Text style={{ marginLeft: 8, fontSize: '14px', fontWeight: 'bold', color: '#eb2f96' }}>
                                {story.story_points}
                              </Text>
                            </div>

                            {story.linked_epic_id && (
                              <div>
                                <Text strong style={{ color: '#fa8c16' }}>Epic:</Text> 
                                <Tag color="orange" style={{ marginLeft: 8 }}>{story.linked_epic_id}</Tag>
                              </div>
                            )}

                            {story.dependencies && story.dependencies.length > 0 && (
                              <div>
                                <Text strong style={{ color: '#f5222d' }}>Dependencies:</Text>
                                <div style={{ marginTop: 4 }}>
                                  {story.dependencies.map((dep: string, i: number) => (
                                    <Tag key={i} color="red">{dep}</Tag>
                                  ))}
                                </div>
                              </div>
                            )}

                            {story.assumptions && story.assumptions.length > 0 && (
                              <div>
                                <Text strong style={{ color: '#faad14' }}>Assumptions:</Text>
                                <ul style={{ margin: '8px 0 0 0', paddingLeft: 24, backgroundColor: '#fffbe6', padding: '12px 24px', borderRadius: '6px', border: '1px solid #ffe58f' }}>
                                  {story.assumptions.map((assumption: string, i: number) => (
                                    <li key={i} style={{ marginBottom: 8, lineHeight: '1.4' }}>
                                      <Text style={{ fontSize: '13px' }}>{assumption}</Text>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </Space>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                </Card>
              )}

              {/* Sprint Planning Section */}
              {project.artifacts.jiraStories.content.sprint_planning && project.artifacts.jiraStories.content.sprint_planning.length > 0 && (
                <Card title="Sprint Planning" style={{ marginBottom: 24 }}>
                  <Row gutter={[16, 16]}>
                    {project.artifacts.jiraStories.content.sprint_planning.map((sprint: any, index: number) => (
                      <Col xs={24} lg={12} key={index}>
                        <Card
                          size="small"
                          style={{ border: '1px solid #d9d9d9' }}
                          title={
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '5px' }}>
                              <Tooltip title={sprint.name} placement="top">
                                <Text strong style={{ color: '#13c2c2', fontSize: '14px', display: 'block', lineHeight: '1.3' }}>
                                  {sprint.name.length > 50 ? `${sprint.name.substring(0, 50)}...` : sprint.name}
                                </Text>
                              </Tooltip>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                                <Tag color="cyan" style={{ margin: 0 }}>{sprint.sprint_id}</Tag>
                                <div style={{ display: 'flex', gap: '4px' }}>
                                  <Tag color="green" style={{ margin: 0 }}>{sprint.duration}</Tag>
                                  <Tag color="gold" style={{ margin: 0 }}>{sprint.total_story_points} pts</Tag>
                                </div>
                              </div>
                            </div>
                          }
                          extra={null}
                        >
                          <Space direction="vertical" size={8} style={{ width: '100%' }}>
                            <div>
                              <Text strong>Rationale:</Text>
                              <br />
                              <Text type="secondary">{sprint.rationale}</Text>
                            </div>
                            <div>
                              <Text strong>Stories:</Text>
                              <div style={{ marginTop: 4 }}>
                                {sprint.stories?.map((storyId: string, i: number) => (
                                  <Tag key={i} color="processing">{storyId}</Tag>
                                ))}
                              </div>
                            </div>
                          </Space>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                </Card>
              )}

              {/* Project Assumptions Section */}
              {project.artifacts.jiraStories.assumptions && project.artifacts.jiraStories.assumptions.length > 0 && (
                <Card title="Project Assumptions" style={{ marginBottom: 24 }}>
                  <div style={{ backgroundColor: '#fffbe6', padding: 16, borderRadius: 8, border: '1px solid #ffe58f' }}>
                    <ul style={{ margin: 0, paddingLeft: 28 }}>
                      {project.artifacts.jiraStories.assumptions.map((assumption: string, index: number) => (
                        <li key={index} style={{ marginBottom: 12, lineHeight: '1.5' }}>
                          <Text>{assumption}</Text>
                        </li>
                      ))}
                    </ul>
                  </div>
                </Card>
              )}

              {/* Project Constraints Section */}
              {project.artifacts.jiraStories.constraints && project.artifacts.jiraStories.constraints.length > 0 && (
                <Card title="Project Constraints" style={{ marginBottom: 24 }}>
                  <div style={{ backgroundColor: '#fff2e8', padding: 16, borderRadius: 8, border: '1px solid #ffcc99' }}>
                    <Space direction="vertical" size={8} style={{ width: '100%' }}>
                      {project.artifacts.jiraStories.constraints.map((constraint: string, index: number) => (
                        <Tag key={index} color="orange" style={{ marginBottom: 4, display: 'block', whiteSpace: 'normal', height: 'auto', padding: '8px 12px', fontSize: '13px' }}>
                          {constraint}
                        </Tag>
                      ))}
                    </Space>
                  </div>
                </Card>
              )}
            </div>
          ) : (
            /* Fallback for old format */
            Array.isArray(project.artifacts.jiraStories) && project.artifacts.jiraStories.length > 0 ? (
              <Space direction="vertical" size={16} style={{ width: '100%' }}>
                {project.artifacts.jiraStories.map((story: JiraStory, index: number) => (
                  <Card key={index} size="small" title={story.title} extra={<Tag color="blue">Priority: {story.priority}</Tag>}>
                    <Title level={5} style={{ marginTop: 0 }}>{story.userStory}</Title>
                    <Text strong>Acceptance Criteria:</Text>
                    <ul style={{ margin: 0, paddingLeft: 20 }}>
                      {Array.isArray(story.acceptanceCriteria) 
                        ? story.acceptanceCriteria.map((criteria, i) => (
                            <li key={i}>
                              <Text>{criteria}</Text>
                            </li>
                          ))
                        : story.acceptanceCriteria && (
                            <li>
                              <Text>{story.acceptanceCriteria}</Text>
                            </li>
                          )
                      }
                    </ul>
                    <Divider />
                    <Text>Story Points: {story.storyPoints}</Text>
                    {story.linkedRequirementId && story.linkedRequirementId.length > 0 && (
                      <>
                        <Divider />
                        <Text strong>Linked Requirements: </Text>
                        <Text>{Array.isArray(story.linkedRequirementId) ? story.linkedRequirementId.join(', ') : story.linkedRequirementId}</Text>
                      </>
                    )}
                    {story.dependencies && story.dependencies.length > 0 && (
                      <>
                        <Divider />
                        <Text strong>Dependencies: </Text>
                        <Text>{story.dependencies.join(', ')}</Text>
                      </>
                    )}
                  </Card>
                ))}
              </Space>
            ) : (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '200px',
                padding: '40px',
                textAlign: 'center'
              }}>
                <Space direction="vertical" size={16} style={{ alignItems: 'center' }}>
                  <Alert 
                    message="No Jira stories created yet" 
                    type="info" 
                    showIcon 
                    style={{ 
                      marginBottom: 0,
                      textAlign: 'center'
                    }} 
                  />
                  <Button
                    type="primary"
                    onClick={onGenerateJiraStories}
                    loading={generateJiraStoriesLoading}
                    size="large"
                    style={{
                      backgroundColor: '#dc3545',
                      borderColor: '#dc3545'
                    }}
                  >
                    Generate Jira Stories
                  </Button>
                </Space>
              </div>
            )
          )}
        </div>
      ) : (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '200px',
          padding: '40px',
          textAlign: 'center'
        }}>
          <Space direction="vertical" size={16} style={{ alignItems: 'center' }}>
            <Alert 
              message="No Jira stories created yet" 
              type="info" 
              showIcon 
              style={{ 
                marginBottom: 0,
                textAlign: 'center'
              }} 
            />
            <Button
              type="primary"
              onClick={onGenerateJiraStories}
              loading={generateJiraStoriesLoading}
              size="large"
              style={{
                backgroundColor: '#dc3545',
                borderColor: '#dc3545'
              }}
            >
              Generate Jira Stories
            </Button>
          </Space>
        </div>
      )}
    </div>
  );
};

export default JiraStoriesViewer;